'use client';

import type React from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Project } from '@/app/models/project';
import type { RuleOverheadProject } from '@/app/models/ruleOverheadProject';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useCreateOrUpdateOverHeadProjectRuleMutation } from '@/app/services/ruleOverHeadProjectApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, TextField, Typography, useTheme } from '@mui/material';

interface PriorityProjectProps {
  rule: RuleOverheadProject | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditPriorityAllocation(props: PriorityProjectProps) {
  const theme = useTheme();
  const { rule: ruleProject, setDrawerOpen } = props;
  const { t } = useTranslation(['allocationPriority', 'common']);

  const [selectedPriority, setSelectedPriority] = useState<number | null>(ruleProject?.priority || null);
  const [projectSearch, setProjectSearch] = useState(ruleProject?.project.code || '');
  const [project, setProject] = useState<Project | null>(ruleProject?.project || null);

  const { data: projects, isFetching: isLoadingProjects } = useSearchProjectsQuery(
    {
      searchTerm: projectSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
      bringClosed: true,
    },
    { skip: projectSearch.length < 1 },
  );

  const [addOrUpdateRuleProjectPriority] = useCreateOrUpdateOverHeadProjectRuleMutation();

  const handleSubmit = async () => {
    let response = {};

    if (!selectedPriority || !project) return;

    setDrawerOpen(false);
    toast.info(
      t('common:processing.wait.a.moment', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );
    response = await addOrUpdateRuleProjectPriority({ priority: selectedPriority, projectCode: project?.code });
    if ('data' in response) {
      toast.dismiss();
      toast.success(
        t('add.rule.allocation.priority.success', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    } else {
      toast.error(
        t('common:processing.failed', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle
          title={t('title.new.rule.priority.allocation')}
          subtitle={t('new.rule.priority.allocation.message') as string}
        />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: theme.spacing(2),
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:project')}
            </Typography>
            <Box sx={{ display: 'flex', width: '100%' }}>
              {!ruleProject ? (
                <GenericAutoComplete
                  value={project}
                  onChange={(value) => {
                    if (value) setProject(value);
                  }}
                  equalityTest={(option, value) => option?.code === value?.code}
                  getOptionLabel={(option) => `${option?.code} - ${option?.name}`}
                  onSearch={(value) => {
                    setProjectSearch(value);
                  }}
                  options={projectSearch !== '' && projects?.content ? projects?.content : []}
                  placeholder={t('enter.project.name.or.code') || ''}
                  loading={isLoadingProjects}
                  sx={{ width: '100%' }}
                />
              ) : (
                <TextField value={project?.code} variant="outlined" size="small" disabled sx={{ width: '100%' }} />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'block', minWidth: theme.spacing(31) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:priority')}
            </Typography>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <TextField
                id="outlined-number"
                type="number"
                size="small"
                variant="outlined"
                required
                inputProps={{ min: 1, max: 100 }}
                value={selectedPriority}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value) {
                    setSelectedPriority(Number(event.target.value));
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          disabled={!selectedPriority || selectedPriority === 0 || !project}
        />
      </Box>
    </Box>
  );
}
